/**
 * Onboarding do médico (web) — espelha o gate do mobile + dados Memed.
 */

export const DOCTOR_ONBOARDING_CACHE_KEY = (userId: string) =>
  `doctor_onboarding_${userId}`;

export const DOCTOR_SPECIALTIES = [
  'Cardiologia',
  'Neurologia',
  'Ortopedia',
  'Pediatria',
  'Clínico Geral',
  'Oftalmologia',
  'Dermatologia',
  'Psiquiatria',
  'Outras',
] as const;

export const BRAZIL_UF_OPTIONS = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
] as const;

export const DOCTOR_ONBOARDING_DOCUMENT_TYPES = [
  'CRM',
  'SPECIALTY_CERTIFICATE',
  'INSCRIPTION_PROOF',
  'CONFIRMATION_VIDEO',
] as const;

export type DoctorOnboardingDocumentType =
  (typeof DOCTOR_ONBOARDING_DOCUMENT_TYPES)[number];

export const doctorOnboardingSteps = [
  {
    id: 'identity',
    title: 'Dados para prescrição',
    subtitle: 'Informações exigidas pela Memed',
  },
  {
    id: 'specialty',
    title: 'Especialidade',
    subtitle: 'Área de atuação',
  },
  {
    id: 'credentials',
    title: 'Credenciais',
    subtitle: 'Documentos e vídeo de confirmação',
  },
] as const;

export type DoctorOnboardingProfile = {
  id?: string;
  specialty?: string | null;
  status?: string | null;
  cpf?: string | null;
  dateOfBirth?: string | null;
  sex?: string | null;
  phone?: string | null;
  officeStreet?: string | null;
  officeCity?: string | null;
  officeState?: string | null;
  isApproved?: boolean;
};

/** Mesma regra do mobile: onboarding enquanto docs ainda não foram enviados. */
export function doctorNeedsOnboarding(
  profile: DoctorOnboardingProfile | null | undefined,
): boolean {
  if (!profile) return true;
  if (!profile.specialty) return true;
  const status = String(profile.status || '').toUpperCase();
  return status === '' || status === 'DOCUMENTS_NOT_SENT';
}

export function doctorHasMemedIdentity(
  profile: Pick<
    DoctorOnboardingProfile,
    'cpf' | 'dateOfBirth' | 'sex' | 'phone' | 'officeCity' | 'officeState'
  >,
): boolean {
  const cpfDigits = String(profile.cpf || '').replace(/\D/g, '');
  const dob = String(profile.dateOfBirth || '').slice(0, 10);
  const sex = String(profile.sex || '')
    .trim()
    .toUpperCase();
  const phoneDigits = String(profile.phone || '').replace(/\D/g, '');
  const city = String(profile.officeCity || '').trim();
  const uf = String(profile.officeState || '')
    .trim()
    .toUpperCase();
  return (
    cpfDigits.length === 11 &&
    /^\d{4}-\d{2}-\d{2}$/.test(dob) &&
    (sex === 'M' || sex === 'F') &&
    phoneDigits.length >= 10 &&
    city.length > 0 &&
    uf.length === 2
  );
}

export function hasAllDoctorOnboardingDocuments(
  documents: Array<{ documentType?: string | null }>,
): boolean {
  const types = new Set(
    documents.map((d) => String(d.documentType || '').toUpperCase()).filter(Boolean),
  );
  return DOCTOR_ONBOARDING_DOCUMENT_TYPES.every((type) => types.has(type));
}
